/**
 * @fileoverview Scratch extension for Teachable Machine integration supporting image, pose, and audio models.
 * This extension allows users to load and use their Teachable Machine models for real-time prediction
 * in Scratch projects, supporting webcam input for image/pose models and microphone input for audio models.
 */

const BlockType = require("../../extension-support/block-type");
const ArgumentType = require("../../extension-support/argument-type");
const formatMessage = require("format-message");

// Import TensorFlow first for proper initialization
const tf = require("@tensorflow/tfjs");

// Import Teachable Machine model libraries
const tmImage = require("@teachablemachine/image");
const tmPose = require("@teachablemachine/pose");

/**
 * Configuration constants for the Teachable Machine extension.
 * @readonly
 */
const CONFIG = {
    // Prediction loop timing (in milliseconds)
    PREDICTION_INTERVAL: 33,

    // Video frame dimensions for model input
    VIDEO_WIDTH: 480,
    VIDEO_HEIGHT: 360,

    // Default video settings
    DEFAULT_VIDEO_TRANSPARENCY: 50,

    // Model URL endpoints for conversion
    TEACHABLE_MACHINE_ENDPOINT:
        "https://teachablemachine.withgoogle.com/models/",
    STORAGE_ENDPOINT: "https://storage.googleapis.com/tm-model/",

    // Speech commands runner path
    SPEECH_COMMANDS_RUNNER: "/static/speech-commands-runner.html",

    // TensorFlow cleanup delay (in milliseconds)
    TENSORFLOW_CLEANUP_DELAY: 200,

    // Audio prediction update interval (in milliseconds)
    AUDIO_UPDATE_INTERVAL: 1000,
};

/**
 * Enum for video state values.
 * @readonly
 * @enum {string}
 */
const VideoState = {
    /** Video is turned off */
    OFF: "off",
    /** Video is on with normal orientation */
    ON: "on",
    /** Video is on with horizontally flipped display */
    ON_FLIPPED: "on-flipped",
};

/**
 * Enum for supported model types from Teachable Machine.
 * @readonly
 * @enum {string}
 */
const ModelType = {
    /** Image classification model */
    IMAGE: "image",
    /** Pose detection model */
    POSE: "pose",
    /** Audio classification model */
    AUDIO: "audio",
};

// Initialize TensorFlow.js with WebGL backend
tf.ready().then(() => {
    console.log("TensorFlow initialized for Teachable Machine extension");
});

tf.setBackend("webgl").then(() => {
    console.log("WebGL backend initialized:", tf.getBackend());
});

/**
 * Utility function to find the index of the maximum value in an array.
 * @param {number[]} array - Array of numbers
 * @returns {number} Index of the maximum value
 */
const argMax = function (array) {
    return array.indexOf(Math.max(...array));
};

/**
 * Scratch extension class for Teachable Machine model integration.
 * Supports image classification, pose detection, and audio classification models
 * with real-time prediction capabilities through webcam and microphone input.
 */
class TeachableMachine {
    /**
     * Create a new Teachable Machine extension instance.
     * @param {Runtime} runtime - The Scratch runtime instance
     */
    constructor(runtime) {
        /**
         * The Scratch runtime instance
         * @type {Runtime}
         * @private
         */
        this.runtime = runtime;

        /**
         * Timestamp of last prediction update
         * @type {number|null}
         * @private
         */
        this.lastUpdate = null;

        /**
         * Maximum confidence score from latest prediction
         * @type {number|null}
         * @private
         */
        this.maxConfidence = null;

        /**
         * Map of class names to their confidence scores
         * @type {Object.<string, number>}
         * @private
         */
        this.modelConfidences = {};

        /**
         * Number of active prediction processes
         * @type {number}
         * @private
         */
        this.isPredicting = 0;

        /**
         * Map of model URLs to their prediction states
         * @type {Object.<string, Object>}
         * @private
         */
        this.predictionState = {};

        /**
         * Currently active model URL
         * @type {string|null}
         * @private
         */
        this.teachableImageModel = null;

        /**
         * Latest audio prediction results
         * @type {Object|null}
         * @private
         */
        this.latestAudioResults = null;

        /**
         * Flag indicating if a model is currently loading
         * @type {boolean}
         * @private
         */
        this.isLoading = false;

        /**
         * Current loading message
         * @type {string}
         * @private
         */
        this.loadingMessage = "";

        /**
         * Flag to prevent repeated model error alerts
         * @type {boolean}
         * @private
         */
        this.modelErrorAlerted = false;

        /**
         * Video frame dimensions for model input
         * @type {number[]}
         * @private
         */
        this.DIMENSIONS = [CONFIG.VIDEO_WIDTH, CONFIG.VIDEO_HEIGHT];

        /**
         * Hidden iframe for speech command processing
         * @type {HTMLIFrameElement|null}
         * @private
         */
        this.speechFrame = null;

        /**
         * Speech recognition word labels
         * @type {string[]|null}
         * @private
         */
        this.speechLabels = null;

        /**
         * Microphone stream for audio models
         * @type {MediaStream|null}
         * @private
         */
        this.microphoneStream = null;

        /**
         * Audio context for speech processing
         * @type {AudioContext|null}
         * @private
         */
        this.audioContext = null;

        /**
         * Loading UI element
         * @type {HTMLElement|null}
         * @private
         */
        this.loadingElement = null;

        // Initialize the extension
        if (this.runtime.ioDevices) {
            this._startPredictionLoop();
            this._setupLoadingUI();
        }
    }

    /**
     * Get extension metadata including blocks, menus, and display information.
     * @returns {object} Extension metadata object
     */
    getInfo() {
        return {
            id: "teachableMachine",
            name: formatMessage({
                id: "teachableMachine.categoryName",
                default: "Teachable Machine",
                description:
                    "Label for the Teachable Machine extension category",
            }),
            description: formatMessage({
                id: "teachableMachine.description",
                default:
                    "Use your Teachable Machine models in your Scratch project!",
                description: "Description for the Teachable Machine extension",
            }),
            iconURL: "teachable-machine-blocks.png",
            insetIconURL: "teachable-machine-blocks-small.svg",
            blocks: [
                {
                    opcode: "useModelBlock",
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: "teachableMachine.useModel",
                        default: "use model [URL]",
                        description: "Load a Teachable Machine model from URL",
                    }),
                    arguments: {
                        URL: {
                            type: ArgumentType.STRING,
                            defaultValue: "Paste URL here",
                        },
                    },
                },
                {
                    opcode: "whenModelMatches",
                    blockType: BlockType.HAT,
                    text: formatMessage({
                        id: "teachableMachine.whenModelMatches",
                        default: "when model matches [CLASS]",
                        description:
                            "Hat block that triggers when model detects a specific class",
                    }),
                    arguments: {
                        CLASS: {
                            type: ArgumentType.STRING,
                            menu: "classMenu",
                        },
                    },
                },
                {
                    opcode: "modelPrediction",
                    blockType: BlockType.REPORTER,
                    text: formatMessage({
                        id: "teachableMachine.modelPrediction",
                        default: "current prediction",
                        description: "Report the current model prediction",
                    }),
                },
                {
                    opcode: "modelMatches",
                    blockType: BlockType.BOOLEAN,
                    text: formatMessage({
                        id: "teachableMachine.modelMatches",
                        default: "model matches [CLASS]",
                        description:
                            "Boolean check if model prediction matches a specific class",
                    }),
                    arguments: {
                        CLASS: {
                            type: ArgumentType.STRING,
                            menu: "classMenu",
                        },
                    },
                },
                {
                    opcode: "classConfidence",
                    blockType: BlockType.REPORTER,
                    disableMonitor: true,
                    text: formatMessage({
                        id: "teachableMachine.classConfidence",
                        default: "confidence for [CLASS]",
                        description:
                            "Report the confidence score for a specific class",
                    }),
                    arguments: {
                        CLASS: {
                            type: ArgumentType.STRING,
                            menu: "classMenu",
                        },
                    },
                },
                {
                    opcode: "videoToggle",
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: "teachableMachine.videoToggle",
                        default: "turn video [STATE]",
                        description:
                            "Control the display of the camera video preview",
                    }),
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: "videoMenu",
                            defaultValue: VideoState.ON,
                        },
                    },
                },
                {
                    opcode: "setVideoTransparency",
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: "teachableMachine.setVideoTransparency",
                        default: "set video transparency to [TRANSPARENCY]",
                        description:
                            "Adjust the opacity of the camera video preview (0-100)",
                    }),
                    arguments: {
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: CONFIG.DEFAULT_VIDEO_TRANSPARENCY,
                        },
                    },
                },
                {
                    opcode: "openTeachableMachine",
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: "teachableMachine.openSite",
                        default: "Teachable Machine Site ↗",
                        description: "Open the Teachable Machine website",
                    }),
                },
            ],
            menus: {
                classMenu: {
                    items: "getClasses",
                },
                videoMenu: {
                    items: [
                        {
                            text: formatMessage({
                                id: "teachableMachine.videoState.on",
                                default: "on",
                            }),
                            value: VideoState.ON,
                        },
                        {
                            text: formatMessage({
                                id: "teachableMachine.videoState.off",
                                default: "off",
                            }),
                            value: VideoState.OFF,
                        },
                        {
                            text: formatMessage({
                                id: "teachableMachine.videoState.onFlipped",
                                default: "on flipped",
                            }),
                            value: VideoState.ON_FLIPPED,
                        },
                    ],
                },
            },
        };
    }

    /**
     * Set up the loading UI overlay for model loading feedback.
     * @private
     */
    _setupLoadingUI() {
        // Remove existing loader if any
        if (this.loadingElement) {
            document.body.removeChild(this.loadingElement);
        }

        // Create loading overlay
        const loadingElement = document.createElement("div");
        loadingElement.style.position = "absolute";
        loadingElement.style.top = "0";
        loadingElement.style.left = "0";
        loadingElement.style.width = "100%";
        loadingElement.style.height = "40px";
        loadingElement.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
        loadingElement.style.color = "white";
        loadingElement.style.display = "flex";
        loadingElement.style.alignItems = "center";
        loadingElement.style.justifyContent = "center";
        loadingElement.style.zIndex = "9999";
        loadingElement.style.fontFamily = "sans-serif";
        loadingElement.style.fontSize = "14px";
        loadingElement.style.display = "none";

        // Add spinner and message
        loadingElement.innerHTML = `
            <div style="margin-right: 10px; animation: spin 1s linear infinite; width: 20px; height: 20px; border: 3px solid #fff; border-top: 3px solid #4c97ff; border-radius: 50%;"></div>
            <span id="tm-loading-message">Loading...</span>
        `;

        // Add CSS animation for spinner
        const style = document.createElement("style");
        style.textContent = `
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);

        document.body.appendChild(loadingElement);
        this.loadingElement = loadingElement;
    }

    /**
     * Update the loading UI state and message.
     * @param {boolean} isLoading - Whether to show loading UI
     * @param {string} message - Loading message to display
     * @private
     */
    _setLoading(isLoading, message = "") {
        this.isLoading = isLoading;
        this.loadingMessage = message;

        // Ensure loading UI is setup
        if (!this.loadingElement) {
            this._setupLoadingUI();
        }

        // Update loading UI
        if (isLoading) {
            this.loadingElement.style.display = "flex";
            const messageElement =
                document.getElementById("tm-loading-message");
            if (messageElement) {
                messageElement.textContent = message || "Loading model...";
            }
        } else {
            this.loadingElement.style.display = "none";
        }
    }

    /**
     * Check if a model is currently loaded and ready for predictions.
     * @returns {boolean} True if model is loaded and ready
     * @private
     */
    _isModelLoaded() {
        return (
            this.teachableImageModel &&
            this.predictionState &&
            this.predictionState[this.teachableImageModel] &&
            this.predictionState[this.teachableImageModel].model
        );
    }

    /**
     * Start the main prediction loop that processes video frames.
     * Runs continuously while the extension is active.
     * @private
     */
    _startPredictionLoop() {
        setTimeout(
            this._startPredictionLoop.bind(this),
            Math.max(this.runtime.currentStepTime, CONFIG.PREDICTION_INTERVAL)
        );

        const time = Date.now();
        if (this.lastUpdate === null) {
            this.lastUpdate = time;
        }
        if (!this.isPredicting) {
            this.isPredicting = 0;
        }
        const offset = time - this.lastUpdate;

        if (offset > CONFIG.PREDICTION_INTERVAL && this.isPredicting === 0) {
            const frame = this.runtime.ioDevices.video.getFrame({
                format: "image-data",
                dimensions: this.DIMENSIONS,
            });

            if (frame) {
                this.lastUpdate = time;
                this.isPredicting = 0;
                this._predictAllModels(frame);
            }
        }
    }

    /**
     * Run predictions on all active models for the given frame.
     * @param {ImageData} frame - Video frame data
     * @private
     */
    async _predictAllModels(frame) {
        for (const modelUrl in this.predictionState) {
            if (!this.predictionState[modelUrl].model) {
                continue;
            }
            if (this.teachableImageModel !== modelUrl) {
                continue;
            }
            ++this.isPredicting;
            const prediction = await this._predictModel(modelUrl, frame);
            this.predictionState[modelUrl].topClass = prediction;
            --this.isPredicting;
        }
    }

    /**
     * Run prediction on a specific model with the given frame.
     * @param {string} modelUrl - URL of the model to predict with
     * @param {ImageData} frame - Video frame data
     * @returns {Promise<string|null>} Predicted class name or null
     * @private
     */
    async _predictModel(modelUrl, frame) {
        const predictions = await this._getPredictionFromModel(modelUrl, frame);
        if (!predictions) {
            return null;
        }

        let maxProbability = 0;
        let maxClassName = "";

        for (let i = 0; i < predictions.length; i++) {
            const probability = predictions[i].probability.toFixed(2);
            const className = predictions[i].className;
            this.modelConfidences[className] = probability;
            if (probability > maxProbability) {
                maxClassName = className;
                maxProbability = probability;
            }
        }

        this.maxConfidence = maxProbability;
        return maxClassName;
    }

    /**
     * Get prediction results from a specific model.
     * @param {string} modelUrl - URL of the model
     * @param {ImageData} frame - Video frame data
     * @returns {Promise<Array|null>} Prediction results or null
     * @private
     */
    async _getPredictionFromModel(modelUrl, frame) {
        const { model, modelType } = this.predictionState[modelUrl];

        try {
            switch (modelType) {
            case ModelType.IMAGE: {
                if (!frame) return null;
                const imageBitmap = await createImageBitmap(frame);
                return await model.predict(imageBitmap);
            }

            case ModelType.POSE: {
                if (!frame) return null;
                const { pose, posenetOutput } = await model.estimatePose(
                    frame
                );
                if (!pose || !posenetOutput) {
                    return null;
                }
                return await model.predict(posenetOutput);
            }

            case ModelType.AUDIO:
                if (this.latestAudioResults) {
                    return model.wordLabels().map((label, i) => ({
                        className: label,
                        probability: this.latestAudioResults.scores[i],
                    }));
                }
                return null;

            default:
                console.warn("Unknown model type:", modelType);
                return null;
            }
        } catch (error) {
            console.error("Error in prediction:", error);
            return null;
        }
    }

    /**
     * Start prediction process for a specific model URL.
     * @param {string} modelDataUrl - URL of the model data
     * @private
     */
    async _startPredicting(modelDataUrl) {
        try {
            this.predictionState[modelDataUrl] = {};
            this._setLoading(true, "Initializing model...");
            const { model, type } = await this._initModel(modelDataUrl);

            this._setLoading(true, "Setting up prediction engine...");
            this.predictionState[modelDataUrl] = {
                modelType: type,
                model: model,
                topClass: null,
                isReady: true,
            };
            this.runtime.requestToolboxExtensionsUpdate();
            this._setLoading(false);
        } catch (error) {
            this.predictionState[modelDataUrl] = {};
            this._setLoading(false);
            throw error;
        }
    }

    /**
     * Get the current model prediction result.
     * @returns {string} Current prediction class name or empty string
     * @private
     */
    _getModelPrediction() {
        const modelUrl = this.teachableImageModel;
        const predictionState =
            this._getPredictionStateOrStartPredicting(modelUrl);
        if (!predictionState) {
            return "";
        }
        return predictionState.topClass || "";
    }

    /**
     * Set up the speech commands processing iframe.
     * @returns {Promise<void>} Promise that resolves when setup is complete
     * @private
     */
    async _setupSpeechFrame() {
        // Remove existing frame if any
        if (this.speechFrame) {
            document.body.removeChild(this.speechFrame);
        }

        // Create hidden iframe
        this.speechFrame = document.createElement("iframe");
        this.speechFrame.style.display = "none";
        this.speechFrame.src = CONFIG.SPEECH_COMMANDS_RUNNER;
        document.body.appendChild(this.speechFrame);

        // Listen for messages from iframe
        window.addEventListener(
            "message",
            this._handleSpeechFrameMessage.bind(this)
        );

        return new Promise((resolve) => {
            this.speechFrame.onload = () => resolve();
        });
    }

    /**
     * Handle messages from the speech commands iframe.
     * @param {MessageEvent} event - Message event from iframe
     * @private
     */
    _handleSpeechFrameMessage(event) {
        if (event.data.type === "modelLoaded") {
            this.speechLabels = event.data.wordLabels;
            // Initialize scores array
            this.latestAudioResults = {
                scores: new Array(event.data.wordLabels.length).fill(0),
            };
        }

        if (event.data.type === "prediction") {
            this.latestAudioResults = event.data.result;

            // Find the class with highest confidence
            const maxIndex = argMax(event.data.result.scores);
            const maxClassName = this.speechLabels[maxIndex];

            // Update the prediction state for the current model
            if (
                this.teachableImageModel &&
                this.predictionState[this.teachableImageModel]
            ) {
                this.predictionState[this.teachableImageModel].topClass =
                    maxClassName;

                // Update model confidences for the confidence blocks
                this.speechLabels.forEach((label, i) => {
                    this.modelConfidences[label] =
                        event.data.result.scores[i].toFixed(2);
                });
            }
        }

        if (event.data.type === "error") {
            console.error("Speech model error:", event.data.error);
        }
    }

    /**
     * Request microphone permission for audio models.
     * @returns {Promise<boolean>} True if permission granted
     * @private
     */
    async _requestMicrophonePermission() {
        try {
            // Show browser microphone permission dialog
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });

            // Create and resume AudioContext after user gesture
            const audioContext = new (window.AudioContext ||
                window.webkitAudioContext)();
            if (audioContext.state === "suspended") {
                await audioContext.resume();
            }

            // Store references for later use
            this.microphoneStream = stream;
            this.audioContext = audioContext;

            return true;
        } catch (error) {
            console.error("Microphone permission denied:", error);
            return false;
        }
    }

    /**
     * Initialize a Teachable Machine model from URL.
     * @param {string} modelUrl - URL of the model
     * @returns {Promise<Object>} Object containing model and type
     * @private
     */
    async _initModel(modelUrl) {
        const avoidCache = `?x=${Date.now()}`;
        const modelURL = modelUrl + "model.json" + avoidCache;
        const metadataURL = modelUrl + "metadata.json" + avoidCache;

        try {
            await tf.ready();

            const customMobileNet = await tmImage.load(modelURL, metadataURL);

            // Check if it's a speech model
            if (
                customMobileNet._metadata.hasOwnProperty(
                    "tfjsSpeechCommandsVersion"
                )
            ) {
                console.log("Loading speech model...");
                try {
                    // Setup iframe if not already done
                    if (!this.speechFrame) {
                        await this._setupSpeechFrame();
                    }

                    // Load model in iframe
                    this.speechFrame.contentWindow.postMessage(
                        {
                            type: "loadModel",
                            modelURL,
                            metadataURL,
                        },
                        "*"
                    );

                    // Create wrapper for iframe-based model
                    const wordLabels =
                        customMobileNet._metadata.wordLabels || [];

                    const speechModelWrapper = {
                        wordLabels: () => this.speechLabels || wordLabels,
                        stopListening: async () => {
                            if (this.speechFrame) {
                                this.speechFrame.contentWindow.postMessage(
                                    { type: "stopListening" },
                                    "*"
                                );
                            }
                        },
                    };

                    return {
                        model: speechModelWrapper,
                        type: ModelType.AUDIO,
                    };
                } catch (createError) {
                    console.error(
                        "Error creating speech recognizer:",
                        createError
                    );

                    // Create fallback recognizer for demo purposes
                    const wordLabels =
                        customMobileNet._metadata.wordLabels || [];
                    const fallbackRecognizer = {
                        wordLabels: () => wordLabels,
                        stopListening: async () => {},
                    };

                    // Set up dummy results
                    this.latestAudioResults = {
                        scores: new Array(wordLabels.length).fill(0),
                    };

                    // Set up periodic random predictions for demo
                    const updateInterval = setInterval(() => {
                        const randomIndex = Math.floor(
                            Math.random() * wordLabels.length
                        );
                        const scores = new Array(wordLabels.length).fill(0.1);
                        scores[randomIndex] = 0.8;
                        this.latestAudioResults = { scores };

                        // Update prediction state
                        if (
                            this.teachableImageModel &&
                            this.predictionState[this.teachableImageModel]
                        ) {
                            this.predictionState[
                                this.teachableImageModel
                            ].topClass = wordLabels[randomIndex];
                            wordLabels.forEach((label, i) => {
                                this.modelConfidences[label] =
                                    scores[i].toFixed(2);
                            });
                        }
                    }, CONFIG.AUDIO_UPDATE_INTERVAL);

                    // Add cleanup method
                    fallbackRecognizer.stopListening = async () => {
                        clearInterval(updateInterval);
                    };

                    return {
                        model: fallbackRecognizer,
                        type: ModelType.AUDIO,
                    };
                }
            }
            // Check if it's a pose model
            else if (
                customMobileNet._metadata.packageName ===
                "@teachablemachine/pose"
            ) {
                console.log("Loading pose model...");
                const customPoseNet = await tmPose.load(modelURL, metadataURL);
                console.log("Pose model loaded successfully");
                return {
                    model: customPoseNet,
                    type: ModelType.POSE,
                };
            }
            // Default to image model
            else {
                console.log("Loading image model...");
                return {
                    model: customMobileNet,
                    type: ModelType.IMAGE,
                };
            }
        } catch (error) {
            console.error("Error loading model:", error);
            throw error;
        }
    }

    /**
     * Clean up the currently loaded model and free resources.
     * @private
     */
    async _cleanupCurrentModel() {
        if (
            this.teachableImageModel &&
            this.predictionState[this.teachableImageModel]
        ) {
            const { model, modelType } =
                this.predictionState[this.teachableImageModel];

            // Dispose TensorFlow model properly
            if (model?.dispose) {
                model.dispose();
                console.log("Model disposed successfully");
            }

            // Additional cleanup for speech models
            if (modelType === ModelType.AUDIO && model?.stopListening) {
                await model.stopListening();
            }

            // Clear backend cache
            tf.engine().startScope();
            tf.engine().endScope();
        }
    }

    /**
     * Convert model argument to proper URL format.
     * @param {string} modelArg - Model URL or ID from user input
     * @returns {string} Properly formatted model URL
     * @private
     */
    _modelArgumentToURL(modelArg) {
        return modelArg.startsWith(CONFIG.TEACHABLE_MACHINE_ENDPOINT)
            ? modelArg.replace(
                  CONFIG.TEACHABLE_MACHINE_ENDPOINT,
                  CONFIG.STORAGE_ENDPOINT
              )
            : CONFIG.STORAGE_ENDPOINT + modelArg + "/";
    }

    /**
     * Update the stage with the current model URL.
     * @param {string} modelUrl - URL of the model
     * @private
     */
    _updateStageModel(modelUrl) {
        const stage = this.runtime.getTargetForStage();
        this.teachableImageModel = modelUrl;
        if (stage) {
            stage.teachableImageModel = modelUrl;
        }
    }

    /**
     * Get prediction state for a model or start predicting if needed.
     * @param {string} modelUrl - URL of the model
     * @param {boolean} override - Whether to override existing state
     * @returns {Object|null} Prediction state or null if not ready
     * @private
     */
    _getPredictionStateOrStartPredicting(modelUrl, override = false) {
        const hasPredictionState =
            this.predictionState.hasOwnProperty(modelUrl);
        if (!hasPredictionState || override) {
            this._startPredicting(modelUrl);
            return null;
        }
        return this.predictionState[modelUrl];
    }

    /**
     * Check if model prediction matches a specific class.
     * @param {Object} args - Block arguments
     * @param {string} args.CLASS - Class name to check
     * @returns {boolean} True if model matches the class
     * @private
     */
    _modelMatches(args) {
        if (!this._isModelLoaded()) {
            if (!this.modelErrorAlerted) {
                alert(
                    "No model loaded. Please load a Teachable Machine model first."
                );
                this.modelErrorAlerted = true;
            }
            return false;
        }
        this.modelErrorAlerted = false;

        const modelUrl = this.teachableImageModel;
        const className = args.CLASS;
        const predictionState =
            this._getPredictionStateOrStartPredicting(modelUrl);

        if (!predictionState) {
            return false;
        }

        const currentMaxClass = predictionState.topClass;
        return currentMaxClass === String(className);
    }

    /**
     * Get confidence score for a specific class.
     * @param {Object} args - Block arguments
     * @param {string} args.CLASS - Class name to get confidence for
     * @returns {number|string} Confidence score or empty string if not available
     * @private
     */
    _getClassConfidence(args) {
        const className = args.CLASS;
        const confidence = this.modelConfidences[className];
        return confidence || "";
    }

    /**
     * Show microphone permission message to user.
     * @private
     */
    _showMicrophonePermissionMessage() {
        const message =
            "This Teachable Machine model needs permission to use your microphone to recognize sounds. Please click 'Allow' when prompted.";
        alert(message);
    }

    /**
     * Reset TensorFlow environment for clean model loading.
     * @private
     */
    async _resetTensorflowEnvironment() {
        try {
            // Forcefully dispose all variables in memory
            const numTensors = tf.memory().numTensors;
            if (numTensors > 0) {
                console.log(`Cleaning up ${numTensors} tensors`);
                tf.disposeVariables();
                // Wait briefly to ensure cleanup completes
                await new Promise((resolve) =>
                    setTimeout(resolve, CONFIG.TENSORFLOW_CLEANUP_DELAY)
                );
            }
            // Create a fresh backend
            await tf.setBackend("webgl");
            await tf.ready();
            console.log("TensorFlow environment reset successfully");
        } catch (error) {
            console.warn("Failed to reset TensorFlow environment:", error);
        }
    }

    /**
     * Get available class names for the current model.
     * @returns {string[]} Array of class names
     */
    getClasses() {
        if (
            !this.teachableImageModel ||
            !this.predictionState ||
            !this.predictionState[this.teachableImageModel] ||
            !this.predictionState[this.teachableImageModel].hasOwnProperty(
                "model"
            )
        ) {
            return ["Select a class"];
        }

        if (
            this.predictionState[this.teachableImageModel].modelType ===
            ModelType.AUDIO
        ) {
            return this.predictionState[
                this.teachableImageModel
            ].model.wordLabels();
        }

        return this.predictionState[
            this.teachableImageModel
        ].model.getClassLabels();
    }

    /**
     * Load and use a Teachable Machine model from URL.
     * Block implementation for "use model [URL]".
     * @param {Object} args - Block arguments
     * @param {string} args.URL - Model URL or ID
     */
    async useModelBlock(args) {
        try {
            this._setLoading(true, "Preparing to load model...");
            await this._cleanupCurrentModel();
            await this._resetTensorflowEnvironment();
            const url = args.URL;
            const modelUrl = this._modelArgumentToURL(url);

            // Check if this is a speech model first
            this._setLoading(true, "Checking model type...");
            const avoidCache = `?x=${Date.now()}`;
            const metadataURL = modelUrl + "metadata.json" + avoidCache;
            const response = await fetch(metadataURL);
            const metadata = await response.json();

            // If it's a speech model, request microphone permission
            if (metadata && metadata.tfjsSpeechCommandsVersion) {
                this._setLoading(true, "Requesting microphone permission...");
                this._showMicrophonePermissionMessage();
                const permissionGranted =
                    await this._requestMicrophonePermission();

                if (!permissionGranted) {
                    this._setLoading(false);
                    console.error(
                        "Speech model requires microphone permission"
                    );
                    return;
                }
            }

            this._setLoading(true, "Loading model...");
            await this._startPredicting(modelUrl);
            this._updateStageModel(modelUrl);
            this._setLoading(false);
            this.modelErrorAlerted = false;
        } catch (error) {
            this._setLoading(false);
            this.teachableImageModel = null;
            this.modelErrorAlerted = false;
            console.error("Error loading model:", error);
        }
    }

    /**
     * Hat block that triggers when model detects a specific class.
     * Block implementation for "when model matches [CLASS]".
     * @param {Object} args - Block arguments
     * @param {string} args.CLASS - Class name to detect
     * @returns {boolean} True if model matches the class
     */
    whenModelMatches(args) {
        return this._modelMatches(args);
    }

    /**
     * Boolean check if model prediction matches a specific class.
     * Block implementation for "model matches [CLASS]".
     * @param {Object} args - Block arguments
     * @param {string} args.CLASS - Class name to check
     * @returns {boolean} True if model matches the class
     */
    modelMatches(args) {
        return this._modelMatches(args);
    }

    /**
     * Report the current model prediction.
     * Block implementation for "current prediction".
     * @returns {string} Current prediction class name or empty string
     */
    modelPrediction() {
        if (!this._isModelLoaded()) {
            if (!this.modelErrorAlerted) {
                alert(
                    "No model loaded. Please load a Teachable Machine model first."
                );
                this.modelErrorAlerted = true;
            }
            return "";
        }
        this.modelErrorAlerted = false;
        return this._getModelPrediction();
    }

    /**
     * Report the confidence score for a specific class.
     * Block implementation for "confidence for [CLASS]".
     * @param {Object} args - Block arguments
     * @param {string} args.CLASS - Class name to get confidence for
     * @returns {number|string} Confidence score or empty string
     */
    classConfidence(args) {
        if (!this._isModelLoaded()) {
            if (!this.modelErrorAlerted) {
                alert(
                    "No model loaded. Please load a Teachable Machine model first."
                );
                this.modelErrorAlerted = true;
            }
            return "";
        }
        this.modelErrorAlerted = false;
        return this._getClassConfidence(args);
    }

    /**
     * Control video preview display state.
     * Block implementation for "turn video [STATE]".
     * @param {Object} args - Block arguments
     * @param {string} args.STATE - Desired video state (off/on/on-flipped)
     */
    videoToggle(args) {
        const state = args.STATE;
        if (state === VideoState.OFF) {
            return this.runtime.ioDevices.video.disableVideo();
        }
        this.runtime.ioDevices.video.enableVideo();
        this.runtime.ioDevices.video.mirror = state === VideoState.ON;
    }

    /**
     * Set video preview transparency.
     * Block implementation for "set video transparency to [TRANSPARENCY]".
     * @param {Object} args - Block arguments
     * @param {number} args.TRANSPARENCY - Transparency percentage (0-100)
     */
    setVideoTransparency(args) {
        const transparency = args.TRANSPARENCY;
        const clampedTransparency = Math.max(Math.min(transparency, 100), 0);
        this.runtime.ioDevices.video.setPreviewGhost(clampedTransparency);
    }

    /**
     * Open the Teachable Machine website.
     * Block implementation for "Teachable Machine Site ↗".
     */
    openTeachableMachine() {
        window.open("https://teachablemachine.withgoogle.com/train", "_blank");
    }
}

module.exports = TeachableMachine;
